import React from "react"
import Chart from "react-apexcharts"
import { getWeekTooltipLabel } from "utils/dateUtil"

const AreaLineChart = ({
  title,
  data,
  data2 = [],
  allWeeks = [],
  dateByWeek = {},
  prevDateByWeek = {},
  pound,
}) => {
  const gradientColors = pound ? ["#6C63FF", "#48CAE4"] : ["#F97316", "#FBBF24"]

  const hasSecond = data2?.length > 0

  const actualDates = allWeeks.map(w => dateByWeek[w] || null)
  const actualDates2 = allWeeks.map(w => prevDateByWeek[w] || null)
  const xCategories = allWeeks.map(w => `${w}`)

  // Derive year numbers from the date maps (format "DD/MM/YYYY")
  const currentYear = Object.values(dateByWeek)[0]?.split("/")?.[2] || "Current Year"
  const previousYear = Object.values(prevDateByWeek)[0]?.split("/")?.[2] || "Previous Year"

  const series = [
    { name: currentYear, data },
    ...(hasSecond ? [{ name: previousYear, data: data2 }] : []),
  ]

  const options = {
    chart: {
      type: "area",
      toolbar: { show: false },
      background: "transparent",
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 900,
        animateGradually: { enabled: true, delay: 120 },
        dynamicAnimation: { enabled: true, speed: 400 },
      },
      dropShadow: {
        enabled: true,
        color: gradientColors[0],
        top: 8,
        blur: 10,
        opacity: 0.25,
      },
    },

    dataLabels: { enabled: false },

    colors: hasSecond ? [gradientColors[0], "#FF3B7F"] : [gradientColors[0]],

    stroke: {
      curve: "smooth",
      width: hasSecond ? [3, 2.5] : 3,
      colors: hasSecond ? [gradientColors[0], "#FF3B7F"] : [gradientColors[0]],
      dashArray: hasSecond ? [0, 5] : [0],
    },

    markers: {
      size: 4,
      colors: hasSecond ? [gradientColors[0], "#FF3B7F"] : [gradientColors[0]],
      strokeColors: ["#fff"],
      strokeWidth: 2,
      hover: { size: 7 },
    },

    fill: {
      type: "gradient",
      gradient: {
        type: "vertical",
        shadeIntensity: 0,
        gradientToColors: hasSecond
          ? [gradientColors[1], "#FF3B7F"]
          : [gradientColors[1]],
        inverseColors: false,
        opacityFrom: hasSecond ? [0.35, 0.12] : 0.45,
        opacityTo: 0.02,
        stops: [0, 95, 100],
      },
    },

    xaxis: {
      categories: xCategories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        rotate: -45,
        style: { colors: "#94a3b8", fontSize: "11px", fontWeight: 500 },
      },
      tooltip: {
        enabled: false, // 👈 THIS removes the extra tooltip
      },
    },

    yaxis: {
      title: {
        text: title,
        style: { color: "#94a3b8", fontSize: "11px", fontWeight: 600 },
      },
      labels: {
        style: { colors: "#94a3b8", fontSize: "11px" },
        formatter: val =>
          pound
            ? `£${val.toLocaleString("en-GB")}`
            : `${val.toLocaleString("en-GB")}%`,
      },
    },

    grid: {
      borderColor: "#f1f5f9",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 0, right: 16, bottom: 0, left: 8 },
    },

    tooltip: {
      theme: "dark",
      shared: true, // ← show both lines in one tooltip
      intersect: false,
      style: { fontSize: "12px" },
      x: {
        formatter: (_val, { dataPointIndex }) => {
          const curLabel = getWeekTooltipLabel(dataPointIndex, actualDates[dataPointIndex])
          const prevLabel = getWeekTooltipLabel(dataPointIndex, actualDates2[dataPointIndex])
          return `${currentYear} : ${curLabel}<br/>${previousYear} : ${prevLabel}`
        },
      },
      y: {
        formatter: val =>
          val !== null && val !== undefined
            ? pound
              ? `£${val.toLocaleString("en-GB")}`
              : `${val.toLocaleString("en-GB")}%`
            : "N/A",
      },
    },

    // ← show legend only when second series is present
    legend: {
      position: "bottom", //
      horizontalAlign: "center",
      fontSize: "12px",
      fontWeight: 600,
      labels: { colors: ["#1e293b", "#64748b"] },
      markers: { radius: 3, width: 10, height: 3 },
    },
  }

  return (
    <div
      style={{
        background: "linear-gradient(145deg, #ffffff 0%, #f8faff 100%)",
        borderRadius: "16px",
        padding: "20px 16px 8px",
        boxShadow:
          "0 4px 24px rgba(108,99,255,0.08), 0 1px 4px rgba(0,0,0,0.04)",
        border: "1px solid #ede9fe",
        position: "relative",
        overflow: "hidden",
        height: "100%",
      }}
      className="mb-4"
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background: `linear-gradient(90deg, ${gradientColors[0]}, ${gradientColors[1]})`,
          borderRadius: "16px 16px 0 0",
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: "4px",
          paddingLeft: "8px",
        }}
      >
        <div
          style={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${gradientColors[0]}, ${gradientColors[1]})`,
            boxShadow: `0 0 8px ${gradientColors[0]}88`,
          }}
        />
        <span
          style={{
            fontSize: "15px",
            fontWeight: 700,
            color: "#1e293b",
            letterSpacing: "-0.2px",
          }}
        >
          {title}
        </span>
      </div>

      <Chart type="area" options={options} series={series} height={320} />
    </div>
  )
}

export default AreaLineChart
